import BuzzInButton from "app/buzz-in/BuzzInButton";
import { BuzzInProvider } from "app/buzz-in/BuzzInProvider";
import { BuzzInAction, WordCompletionStatus } from "app/buzz-in/buzzInState";
import {
  useBuzzInActionDispatch as useBuzzInDispatch,
  useBuzzInState,
} from "app/buzz-in/useBuzzIn";
import { BUZZ_IN_SETTINGS } from "app/data/game-settings";
import { RoundType, type WordData } from "app/game-data/useFetchGameData";
import useGameData from "app/game-data/useGameData";
import { useGameState } from "app/game/useGame";
import IconPlay from "app/icons/IconPlay";
import getWordCorrectness from "app/score/getWordCorrectness";
import getWordScore from "app/score/getWordScore";
import { ScoreAction } from "app/score/scoreState";
import { useScoreDispatch, useScoreState } from "app/score/useScore";
import KeyboardGroup from "app/spelling-input/KeyboardGroup";
import MenuButton from "app/ui/MenuButton";
import NextRoundButton from "app/ui/NextRoundButton";
import ProgressBar from "app/ui/ProgressBar";
import RoundIntroScreen from "app/ui/RoundIntroScreen";
import WordSpellingRecord from "app/ui/WordSpellingRecord";
import cn from "app/utils/classnames";
import remap from "app/utils/remap";
import { useInterval } from "app/utils/useInterval";
import usePreloadedAudio from "app/utils/usePreloadedAudio";
import mixpanel from "mixpanel-browser";
import { useEffect, useEffectEvent, useState } from "react";
import { useKey } from "react-use";

interface BuzzInRoundProps {
  words: Array<WordData>;
  roundIndex: number;
  onComplete: () => void;
  className?: string;
}

function BuzzInRound({
  words,
  roundIndex,
  onComplete,
  className,
}: BuzzInRoundProps) {
  const gameData = useGameData();
  const roundState = useBuzzInState();
  const roundDispatch = useBuzzInDispatch();
  const scoreDispatch = useScoreDispatch();
  const scoreState = useScoreState();
  const { inputType } = useGameState();

  // Used to make sure the player has to click to restart the audio if they
  // leave/return to the page while it's playing.
  const [isInteracted, setIsInteracted] = useState(false);

  // Calculate game progress variables
  const wordIndex = roundState.completedWordIndex;
  const currentWordData = words[wordIndex] || words[words.length - 1];
  const roundComplete = roundState.completedWordIndex > words.length - 1;
  const wordCompletion =
    roundState.completedWordStatuses[wordIndex] || WordCompletionStatus.CURRENT;

  const preloadedAudio = usePreloadedAudio();
  const timerDuration = preloadedAudio.duration;
  const isAudioLoaded = preloadedAudio.isLoaded;

  function getNextAudioUrl(): string | undefined {
    if (roundState.completedWordIndex > words.length - 1) {
      return "";
    }
    return words[roundState.completedWordIndex].definitionAudioUrl;
  }

  const nextAudioUrl = getNextAudioUrl() ?? "";
  useEffect(() => {
    preloadedAudio.preload(nextAudioUrl);
  // Linter wanted me to add preloadAudio, but that would open a big can of
  // worms that I don't have time to address rn. Revisit if bugs appear.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextAudioUrl]);

  // Since it takes a moment for the timer duration to load in, make sure to
  // update the remaining time when it does
  useEffect(() => {
    if (
      isAudioLoaded &&
      timerDuration > 0 &&
      wordCompletion === WordCompletionStatus.CURRENT
    ) {
      roundDispatch({
        type: BuzzInAction.TIMER_UPDATED,
        payload: { newValue: timerDuration },
      });
    }
  }, [roundDispatch, timerDuration, isAudioLoaded, wordCompletion]);

  // Max bonus is based on remaining time
  let currentBonus = BUZZ_IN_SETTINGS.maxBonus;
  const graceCutoff = timerDuration - BUZZ_IN_SETTINGS.gracePeriod;
  if (roundState.timeRemaining < graceCutoff) {
    currentBonus = remap(
      roundState.timeRemaining,
      0,
      graceCutoff,
      0,
      BUZZ_IN_SETTINGS.maxBonus,
    );
    currentBonus = Math.ceil(currentBonus);
  }

  // Run timer
  const timerDelay =
    wordCompletion === WordCompletionStatus.LISTENING && isInteracted
      ? BUZZ_IN_SETTINGS.timerInterval * 1000 // useInterval wants ms
      : null;
  useInterval(() => {
    const newTimeRemaining =
      roundState.timeRemaining - BUZZ_IN_SETTINGS.timerInterval;
    if (newTimeRemaining <= 0) {
      roundDispatch({ type: BuzzInAction.TIMER_EXPIRED });
    } else {
      roundDispatch({
        type: BuzzInAction.TIMER_UPDATED,
        payload: { newValue: newTimeRemaining },
      });
    }
  }, timerDelay);

  // On desktop, spacebar can be used as a shortcut to press the buzzer
  const handleSpacePressed = useEffectEvent(() => {
    if (roundComplete) {
      return;
    }

    switch (wordCompletion) {
      case WordCompletionStatus.LISTENING: {
        preloadedAudio.pause();
        roundDispatch({
          type: BuzzInAction.BUZZED_IN,
          payload: { wordIndex: wordIndex },
        });
        break;
      }
      case WordCompletionStatus.CURRENT:
      case WordCompletionStatus.BUZZED_IN: {
        if (!BUZZ_IN_SETTINGS.allowListenAfterBuzzIn) {
          break;
        }
        const progress = preloadedAudio.duration - roundState.timeRemaining;
        preloadedAudio.seek(progress);
        preloadedAudio.play();
        setIsInteracted(true);
        roundDispatch({
          type: BuzzInAction.PRESSED_LISTEN,
          payload: { wordIndex: wordIndex },
        });
        break;
      }
    }
  });

  useKey(" ", handleSpacePressed);

  function handleListenButtonClick() {
    setIsInteracted(true);
    const progress = preloadedAudio.duration - roundState.timeRemaining;
    preloadedAudio.seek(progress);
    preloadedAudio.play();
    roundDispatch({
      type: BuzzInAction.PRESSED_LISTEN,
      payload: { wordIndex: wordIndex },
    });
  }

  function handleBuzzInClick() {
    preloadedAudio.pause();
    roundDispatch({
      type: BuzzInAction.BUZZED_IN,
      payload: { wordIndex: wordIndex },
    });
  }

  function handleSpellingInputSubmit(word: string) {
    const userSpelling = word.toLocaleLowerCase();
    const correctSpelling = currentWordData.wordText.toLocaleLowerCase();
    const match = getWordCorrectness(userSpelling, correctSpelling);
    const score = getWordScore(
      BUZZ_IN_SETTINGS.baseWordPoints + currentBonus,
      match,
    );

    scoreDispatch({
      type: ScoreAction.WORD_SPELLED,
      payload: {
        roundIndex: roundIndex,
        userSpelling: userSpelling,
        correctSpelling: correctSpelling,
        pointValue: score,
      },
    });

    mixpanel.track("spelling_submitted", {
      game_id: gameData?.id,
      level_type: RoundType.BUZZ_IN,
      user_spelling: userSpelling,
      correct_spelling: correctSpelling,
    });

    scoreDispatch({
      type: ScoreAction.POINTS_EARNED,
      payload: { roundIndex: roundIndex, points: score },
    });

    if (score > 0) {
      roundDispatch({ type: BuzzInAction.ANSWERED_CORRECTLY });
    } else {
      roundDispatch({ type: BuzzInAction.ANSWERED_INCORRECTLY });
    }

    preloadedAudio.stop();
  }

  if (roundState.isOnIntro) {
    return (
      <RoundIntroScreen
        roundType={RoundType.BUZZ_IN}
        roundIndex={roundIndex}
        onContinueClick={() =>
          roundDispatch({ type: BuzzInAction.INTRO_CLOSED })
        }
      />
    );
  }

  if (timerDuration === 0) {
    return <></>;
  }

  return (
    <div
      className={cn(
        "h-full",
        "flex flex-col items-center gap-1 lg:gap-5",
        className,
      )}
    >
      <WordSpellingRecord
        numTotalWords={words.length}
        completedSpellings={scoreState.roundScores[roundIndex]?.wordSpellings}
      />
      {roundComplete ? (
        <div className="mt-5">
          <NextRoundButton
            onClick={() => {
              roundDispatch({ type: BuzzInAction.ROUND_COMPLETED });
              onComplete();
            }}
            allowEnter={true}
          />
        </div>
      ) : (
        <>
          <div // GAMEPLAY CONTAINER
            className={cn(
              "w-full",
              "flex flex-col justify-start gap-5",
            )}
          >
            <div // PROGRESS BAR CONTAINER
              className={cn("w-full", "flex flex-col items-center gap-1")}
            >
              <div className={cn("w-full", "flex justify-center")}>
                <span>
                  Time Bonus:{" "}
                  {(BUZZ_IN_SETTINGS.baseWordPoints + currentBonus)
                    .toString()
                    .padStart(2, "0")}
                </span>
                {/* <span className="min-w-17">
                  {formatTime(roundState.timeRemaining)}
                </span> */}
              </div>
              <ProgressBar value={roundState.timeRemaining / timerDuration} />
            </div>
            <div // BUZZ-IN BUTTONS
              className="w-full flex justify-center"
            >
              {/* Bloated conditional block to decide when to show the listen
                  button.
                  Sorry, I know it's horrible. */}
              {roundState.timeRemaining > 0 &&
                (wordCompletion === WordCompletionStatus.CURRENT ||
                  (wordCompletion === WordCompletionStatus.BUZZED_IN &&
                    BUZZ_IN_SETTINGS.allowListenAfterBuzzIn) ||
                  (wordCompletion === WordCompletionStatus.LISTENING &&
                    !isInteracted)) && (
                  <MenuButton
                    className={cn("px-2 py-3", "gap-2", "h-12")}
                    onClick={handleListenButtonClick}
                    elevation="lg"
                    icon={<IconPlay width={32} height={32} />}
                  >
                    Hear Definition
                  </MenuButton>
                )}
              {isInteracted &&
                wordCompletion === WordCompletionStatus.LISTENING && (
                  <BuzzInButton onClick={handleBuzzInClick} />
                )}
            </div>
          </div>
          <div // SPACER
            className={cn(inputType === "touch" ? "grow" : "h-1")}
          />
          <div // INPUT CONTAINER
            className={cn(
              "w-full flex-none shrink basis-0 min-w-0",
              wordCompletion === WordCompletionStatus.BUZZED_IN
                ? "opacity-100 pointer-events-auto"
                : "opacity-0 pointer-events-none",
            )}
          >
            <KeyboardGroup
              onSubmit={handleSpellingInputSubmit}
              hideInput={wordCompletion != WordCompletionStatus.BUZZED_IN}
              heightShrinkCutoff={575}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default function WrappedBuzzInRound(props: BuzzInRoundProps) {
  return (
    <BuzzInProvider key={props.roundIndex} roundIndex={props.roundIndex}>
      <BuzzInRound {...props} />
    </BuzzInProvider>
  );
}
